import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ClipboardCheck, Beaker, BarChart3, Plus, Trash2, CheckCircle2,
  AlertTriangle, Lock, ArrowLeft, Printer, Syringe,
} from 'lucide-react';
import Header from '../components/Header';
import { VialsTab } from '../components/VialsTab';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.sops;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function IPCPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [tab, setTab] = useState('cleaning');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (isPM) axios.get(`${API}/ipc/summary`, { withCredentials: true }).then(r => setSummary(r.data)).catch(() => {});
  }, [isPM]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="ipc-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/sops" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to SOPs
        </Link>

        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: P.color }}>Infection Prevention & Control</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-foreground">IPC Tooling</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            Audit-ready records that pair with the <Link to="/sops/infection-prevention-control" className="underline" style={{ color: P.color }}>Infection Prevention & Control SOP</Link>. Inspectors ask for these three things, in this order.
          </p>
        </div>

        {isPM && summary && <IPCSummaryCards s={summary} />}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto">
          {[
            { id: 'cleaning',  label: 'Daily cleaning checklist', icon: ClipboardCheck },
            { id: 'bottles',   label: 'Antiseptic bottle tracking', icon: Beaker },
            { id: 'vials',     label: 'Multi-dose vial register', icon: Syringe },
            { id: 'audits',    label: 'Monthly IPC audit', icon: BarChart3, pmOnly: true },
          ].filter(t => !t.pmOnly || isPM).map(t => (
            <button key={t.id} data-testid={`ipc-tab-${t.id}`} onClick={() => setTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors"
              style={tab === t.id ? { borderColor: P.color, color: P.color } : { borderColor: 'transparent', color: 'hsl(var(--muted-foreground))' }}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'cleaning' && <CleaningTab isPM={isPM} />}
        {tab === 'bottles'  && <BottlesTab  isPM={isPM} />}
        {tab === 'vials'    && <VialsTab />}
        {tab === 'audits'   && isPM && <AuditsTab />}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Summary cards
// --------------------------------------------------------------------------
function IPCSummaryCards({ s }) {
  const kpis = [
    { label: "Today's cleaning logs", v: s.today_cleaning_logs, color: P.color },
    { label: 'Logs this week',        v: s.week_cleaning_logs,  color: '#0EA5E9' },
    { label: 'Active antiseptic bottles', v: s.active_bottles, color: '#16A34A' },
    { label: 'Expired bottles (action needed)', v: s.expired_bottles, color: s.expired_bottles > 0 ? '#EF4444' : '#64748B' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="ipc-summary">
      {kpis.map((k, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{k.label}</p>
          <p className="font-['Outfit'] text-2xl font-bold mt-1" style={{ color: k.color }}>{k.v}</p>
        </div>
      ))}
      {s.last_audit && (
        <div className="col-span-2 lg:col-span-4 bg-card border border-border rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Last audit</p>
            <p className="font-semibold text-foreground mt-1">{s.last_audit.audit_month} · {s.last_audit.auditor_name} · <span style={{ color: P.color }}>{s.last_audit.overall_score}%</span></p>
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ backgroundColor: s.last_audit.status === 'finalised' ? '#16A34A15' : '#F59E0B15',
                     color: s.last_audit.status === 'finalised' ? '#16A34A' : '#F59E0B' }}>
            {s.last_audit.status}
          </span>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// 1. Daily cleaning checklist
// --------------------------------------------------------------------------
function CleaningTab({ isPM }) {
  const [template, setTemplate] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), room: 'CT Room', shift: 'morning', initials: '', notes: '', tasks: [] });

  const load = async () => {
    const [t, l] = await Promise.all([
      axios.get(`${API}/ipc/cleaning/template`, { withCredentials: true }),
      axios.get(`${API}/ipc/cleaning`, { withCredentials: true }),
    ]);
    setTemplate(t.data.tasks);
    setLogs(l.data);
    setForm(f => ({ ...f, tasks: t.data.tasks.map(name => ({ name, done: false })) }));
  };
  useEffect(() => { load(); }, []);

  const toggle = (i) => setForm(f => ({ ...f, tasks: f.tasks.map((t, idx) => idx === i ? { ...t, done: !t.done } : t) }));
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/ipc/cleaning`, form, { withCredentials: true });
    setOpen(false);
    setForm({ date: today(), room: 'CT Room', shift: 'morning', initials: '', notes: '', tasks: template.map(name => ({ name, done: false })) });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this cleaning log?')) return;
    await axios.delete(`${API}/ipc/cleaning/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="ipc-cleaning-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Daily cleaning logs</h2>
          <p className="text-xs text-muted-foreground">Every room, every shift. Signed by the person who cleaned it.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="cleaning-new-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: P.color }}>
          <Plus className="w-4 h-4" /> New log
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-card border rounded-xl p-5 mb-6 space-y-4" style={{ borderColor: P.ring }} data-testid="cleaning-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Date"><input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={fieldCls} data-testid="cleaning-date" /></Field>
            <Field label="Room">
              <select value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} className={fieldCls} data-testid="cleaning-room">
                {['CT Room', 'US Room 1', 'US Room 2', 'Procedure Suite', 'Waiting Area', 'Dirty Utility'].map(r => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Shift">
              <select value={form.shift} onChange={e => setForm({ ...form, shift: e.target.value })} className={fieldCls} data-testid="cleaning-shift">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="end_of_day">End of day</option>
                <option value="post_procedure">Post-procedure</option>
              </select>
            </Field>
            <Field label="Initials"><input required maxLength={5} value={form.initials} onChange={e => setForm({ ...form, initials: e.target.value.toUpperCase() })} className={fieldCls} placeholder="e.g. NA" data-testid="cleaning-initials" /></Field>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Tasks</p>
            <ul className="space-y-1">
              {form.tasks.map((t, i) => (
                <li key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent/50">
                  <input type="checkbox" checked={t.done} onChange={() => toggle(i)} data-testid={`cleaning-task-${i}`} />
                  <span className="text-sm">{t.name}</span>
                </li>
              ))}
            </ul>
          </div>
          <Field label="Notes (optional)"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={fieldCls} data-testid="cleaning-notes" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
            <button type="submit" data-testid="cleaning-submit" className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: P.color }}>Save log</button>
          </div>
        </form>
      )}

      {logs.length === 0 ? (
        <EmptyState text="No cleaning logs yet." />
      ) : (
        <div className="space-y-2">
          {logs.map(l => (
            <div key={l.log_id} className="bg-card border border-border rounded-xl p-4" data-testid={`cleaning-row-${l.log_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">{l.date}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: P.colorBg, color: P.color }}>{l.room}</span>
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{l.shift}</span>
                  <span className="text-[11px] text-muted-foreground">· {l.initials}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${l.compliance === 100 ? 'text-green-600 bg-green-500/10' : l.compliance >= 70 ? 'text-amber-600 bg-amber-500/10' : 'text-red-600 bg-red-500/10'}`}>
                    {l.compliance}% done
                  </span>
                  {isPM && <button onClick={() => remove(l.log_id)} className="p-1 text-muted-foreground hover:text-red-500" data-testid={`cleaning-del-${l.log_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {l.tasks.map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    {t.done ? <CheckCircle2 className="w-3 h-3 text-green-500" /> : <span className="w-3 h-3 rounded-full border border-border" />}
                    <span className={t.done ? 'text-foreground' : 'text-muted-foreground line-through'}>{t.name}</span>
                  </li>
                ))}
              </ul>
              {l.notes && <p className="mt-2 text-xs text-muted-foreground italic">"{l.notes}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// 2. Antiseptic bottle tracking
// --------------------------------------------------------------------------
function BottlesTab({ isPM }) {
  const [bottles, setBottles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agent: '2% Chlorhexidine + 70% Isopropyl Alcohol', size_ml: 500, batch_number: '', expiry_date: '', opened_date: today(), location: 'CT Room', intended_use: 'multi_use', notes: '' });
  const [filter, setFilter] = useState('');

  const load = async () => {
    const r = await axios.get(`${API}/ipc/bottles${filter ? `?status=${filter}` : ''}`, { withCredentials: true });
    setBottles(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/ipc/bottles`, form, { withCredentials: true });
    setOpen(false);
    setForm({ ...form, batch_number: '', expiry_date: '', opened_date: today(), notes: '' });
    load();
  };

  const discard = async (bottle_id) => {
    const reason = window.prompt('Discard reason? (e.g. 14-day limit reached, dropped, contaminated, label missing)');
    if (!reason) return;
    await axios.post(`${API}/ipc/bottles/${bottle_id}/discard`, { reason }, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="ipc-bottles-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Antiseptic bottle register</h2>
          <p className="text-xs text-muted-foreground">Open date · discard date (+14 days) · batch number · location. SOP §9.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="text-xs bg-background border border-border rounded-lg px-2 py-1.5" data-testid="bottle-filter">
            <option value="">All</option>
            <option value="active">Active only</option>
            <option value="discarded">Discarded</option>
          </select>
          <button onClick={() => setOpen(true)} data-testid="bottle-new-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: P.color }}>
            <Plus className="w-4 h-4" /> Open bottle
          </button>
        </div>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-card border rounded-xl p-5 mb-6 space-y-4" style={{ borderColor: P.ring }} data-testid="bottle-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Agent"><input required value={form.agent} onChange={e => setForm({ ...form, agent: e.target.value })} className={fieldCls} data-testid="bottle-agent" /></Field>
            <Field label="Size (mL)"><input type="number" min={1} required value={form.size_ml} onChange={e => setForm({ ...form, size_ml: parseInt(e.target.value || '0', 10) })} className={fieldCls} data-testid="bottle-size" /></Field>
            <Field label="Batch number"><input required value={form.batch_number} onChange={e => setForm({ ...form, batch_number: e.target.value })} className={fieldCls} data-testid="bottle-batch" /></Field>
            <Field label="Manufacturer expiry"><input type="date" required value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} className={fieldCls} data-testid="bottle-expiry" /></Field>
            <Field label="Date opened"><input type="date" required value={form.opened_date} onChange={e => setForm({ ...form, opened_date: e.target.value })} className={fieldCls} data-testid="bottle-opened" /></Field>
            <Field label="Discard date (auto, +14d)"><input type="date" disabled value={form.opened_date ? addDays(form.opened_date, 14) : ''} className={`${fieldCls} opacity-70`} /></Field>
            <Field label="Location">
              <select value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className={fieldCls} data-testid="bottle-location">
                {['CT Room', 'US Room 1', 'US Room 2', 'Procedure Suite', 'Clean Utility'].map(r => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Intended use">
              <select value={form.intended_use} onChange={e => setForm({ ...form, intended_use: e.target.value })} className={fieldCls} data-testid="bottle-use">
                <option value="multi_use">Multi-use (low-risk procedures)</option>
                <option value="single_use">Single-use (high-risk: spinal / biopsy / immunocompromised)</option>
              </select>
            </Field>
          </div>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={fieldCls} data-testid="bottle-notes" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
            <button type="submit" data-testid="bottle-submit" className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: P.color }}>Register bottle</button>
          </div>
        </form>
      )}

      {bottles.length === 0 ? (
        <EmptyState text="No bottles registered yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Opened</th>
                <th className="px-3 py-2">Discard by</th>
                <th className="px-3 py-2">Agent</th>
                <th className="px-3 py-2">Batch</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Use</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {bottles.map(b => (
                <tr key={b.bottle_id} className="border-b border-border/60" data-testid={`bottle-row-${b.bottle_id}`}>
                  <td className="px-3 py-2 text-foreground">{b.opened_date}</td>
                  <td className="px-3 py-2">
                    <span className={b.is_expired ? 'text-red-600 font-semibold' : 'text-foreground'}>{b.discard_date}</span>
                    {b.is_expired && <AlertTriangle className="inline w-3 h-3 ml-1 text-red-600" />}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[180px]" title={b.agent}>{b.agent}</td>
                  <td className="px-3 py-2 text-foreground font-mono text-xs">{b.batch_number}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{b.location}</td>
                  <td className="px-3 py-2 text-xs">{b.intended_use === 'single_use' ? 'Single' : 'Multi'}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: b.status === 'active' ? (b.is_expired ? '#EF444415' : '#16A34A15') : '#64748B15',
                               color: b.status === 'active' ? (b.is_expired ? '#EF4444' : '#16A34A') : '#64748B' }}>
                      {b.status === 'active' ? (b.is_expired ? 'expired' : 'active') : 'discarded'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isPM && b.status === 'active' && (
                      <button onClick={() => discard(b.bottle_id)} data-testid={`bottle-discard-${b.bottle_id}`}
                        className="text-[11px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">Discard</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// 3. Monthly IPC audit
// --------------------------------------------------------------------------
function AuditsTab() {
  const [template, setTemplate] = useState([]);
  const [audits, setAudits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    const [t, a] = await Promise.all([
      axios.get(`${API}/ipc/audits/template`, { withCredentials: true }),
      axios.get(`${API}/ipc/audits`, { withCredentials: true }),
    ]);
    setTemplate(t.data.sections);
    setAudits(a.data);
  };
  useEffect(() => { load(); }, []);

  const newAudit = () => {
    setForm({
      audit_month: new Date().toISOString().slice(0, 7),
      auditor_name: '',
      sections: template.map(s => ({ id: s.id, score: 100, comments: '' })),
      findings: '', corrective_actions: '',
      next_audit_date: addDays(today(), 30),
      status: 'draft',
    });
    setOpen(true);
  };

  const updateScore = (id, score) => setForm(f => ({ ...f, sections: f.sections.map(s => s.id === id ? { ...s, score } : s) }));
  const updateComment = (id, comments) => setForm(f => ({ ...f, sections: f.sections.map(s => s.id === id ? { ...s, comments } : s) }));

  const submit = async (e, status) => {
    e.preventDefault();
    await axios.post(`${API}/ipc/audits`, { ...form, status }, { withCredentials: true });
    setOpen(false); setForm(null); load();
  };

  const finalise = async (id) => {
    if (!window.confirm('Finalise this audit? It will be locked for edits and available to inspectors.')) return;
    await axios.post(`${API}/ipc/audits/${id}/finalise`, {}, { withCredentials: true });
    load();
  };

  const labelFor = (id) => template.find(s => s.id === id)?.label || id;
  const overall = form ? Math.round(form.sections.reduce((a, s) => a + s.score, 0) / (form.sections.length || 1)) : 0;

  return (
    <div data-testid="ipc-audits-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Monthly IPC audits</h2>
          <p className="text-xs text-muted-foreground">Score each IPC domain · findings · corrective actions · finalise for the audit binder.</p>
        </div>
        <button onClick={newAudit} data-testid="audit-new-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: P.color }}>
          <Plus className="w-4 h-4" /> New audit
        </button>
      </div>

      {open && form && (
        <form className="bg-card border rounded-xl p-5 mb-6 space-y-4" style={{ borderColor: P.ring }} data-testid="audit-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Audit month"><input type="month" required value={form.audit_month} onChange={e => setForm({ ...form, audit_month: e.target.value })} className={fieldCls} data-testid="audit-month" /></Field>
            <Field label="Auditor name"><input required value={form.auditor_name} onChange={e => setForm({ ...form, auditor_name: e.target.value })} className={fieldCls} data-testid="audit-auditor" /></Field>
            <Field label="Next audit due"><input type="date" value={form.next_audit_date} onChange={e => setForm({ ...form, next_audit_date: e.target.value })} className={fieldCls} data-testid="audit-next" /></Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Section scores</p>
              <span className="text-sm font-bold" style={{ color: overall >= 85 ? '#16A34A' : overall >= 60 ? '#F59E0B' : '#EF4444' }}>Overall · {overall}%</span>
            </div>
            <div className="space-y-2">
              {form.sections.map(s => (
                <div key={s.id} className="grid grid-cols-[1fr_180px] gap-3 items-center" data-testid={`audit-section-${s.id}`}>
                  <div>
                    <label className="text-sm text-foreground block">{labelFor(s.id)}</label>
                    <input type="text" placeholder="comments (optional)" value={s.comments}
                      onChange={e => updateComment(s.id, e.target.value)}
                      className="w-full mt-1 text-xs bg-background border border-border rounded-md px-2 py-1" data-testid={`audit-comment-${s.id}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="range" min={0} max={100} value={s.score} onChange={e => updateScore(s.id, parseInt(e.target.value, 10))}
                      className="flex-1" data-testid={`audit-score-${s.id}`} />
                    <span className="text-sm font-semibold w-10 text-right" style={{ color: s.score >= 85 ? '#16A34A' : s.score >= 60 ? '#F59E0B' : '#EF4444' }}>{s.score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Field label="Findings"><textarea rows={3} value={form.findings} onChange={e => setForm({ ...form, findings: e.target.value })} className={fieldCls} data-testid="audit-findings" /></Field>
          <Field label="Corrective actions"><textarea rows={3} value={form.corrective_actions} onChange={e => setForm({ ...form, corrective_actions: e.target.value })} className={fieldCls} data-testid="audit-corrective" /></Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={() => { setOpen(false); setForm(null); }} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
            <button type="button" onClick={(e) => submit(e, 'draft')} className="text-sm font-semibold px-4 py-1.5 rounded-lg border border-border" data-testid="audit-save-draft">Save as draft</button>
            <button type="button" onClick={(e) => submit(e, 'finalised')} data-testid="audit-finalise-now"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: P.color }}>Save & finalise</button>
          </div>
        </form>
      )}

      {audits.length === 0 ? (
        <EmptyState text="No audits recorded yet." />
      ) : (
        <div className="space-y-3">
          {audits.map(a => {
            const locked = a.status === 'finalised';
            return (
              <div key={a.audit_id} className="bg-card border border-border rounded-xl p-5" data-testid={`audit-row-${a.audit_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <h3 className="font-['Outfit'] text-base font-semibold text-foreground">{a.audit_month} · {a.auditor_name}</h3>
                    <p className="text-xs text-muted-foreground">Created {(a.created_at || '').slice(0, 10)} · Next due {a.next_audit_date || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold" style={{ color: a.overall_score >= 85 ? '#16A34A' : a.overall_score >= 60 ? '#F59E0B' : '#EF4444' }}>Overall · {a.overall_score}%</span>
                    <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${locked ? 'text-green-600 bg-green-500/10' : 'text-amber-600 bg-amber-500/10'}`}>
                      {locked && <Lock className="inline w-3 h-3 mr-1" />}{a.status}
                    </span>
                    {!locked && <button onClick={() => finalise(a.audit_id)} data-testid={`audit-finalise-${a.audit_id}`} className="text-[11px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">Finalise</button>}
                    <button onClick={() => window.print()} className="text-[11px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent" title="Print audit"><Printer className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-xs">
                  {a.sections.map(s => (
                    <div key={s.id} className="flex items-center gap-2">
                      <span className="flex-1 text-muted-foreground">{labelFor(s.id)}</span>
                      <span className="font-bold" style={{ color: s.score >= 85 ? '#16A34A' : s.score >= 60 ? '#F59E0B' : '#EF4444' }}>{s.score}%</span>
                    </div>
                  ))}
                </div>
                {(a.findings || a.corrective_actions) && (
                  <div className="mt-3 pt-3 border-t border-border text-xs space-y-1">
                    {a.findings && <p><span className="font-semibold">Findings: </span><span className="text-muted-foreground">{a.findings}</span></p>}
                    {a.corrective_actions && <p><span className="font-semibold">Actions: </span><span className="text-muted-foreground">{a.corrective_actions}</span></p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
const fieldCls = "w-full text-sm bg-background border border-border rounded-lg px-3 py-2";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function EmptyState({ text }) {
  return (
    <div className="bg-card border border-dashed border-border rounded-xl p-10 text-center">
      <Sparkles className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
